import { useRef, useState } from 'react';

/**
 * Tema 6 (segunda ronda de feedback ASI) — "conservar en cada módulo los
 * filtros particulares": cada pantalla de listado (ReporteDetalle,
 * CfdiCatalogo, MatchManual) tenía sus filtros (banco, cuenta, moneda,
 * estado, folio, UUID, monto, búsqueda) en useState puro — se resetean al
 * salir y volver a entrar a la pantalla. Este hook los persiste en
 * localStorage bajo una clave por pantalla+campo, con el mismo criterio
 * defensivo que ya usan EmpresaProvider/FiltroFechaProvider (nunca revienta
 * si localStorage no está disponible — la persistencia es una mejora de UX,
 * no un requisito funcional).
 *
 * No reemplaza useFiltroFecha (ese es un Context global compartido entre
 * pantallas a propósito — el rango de fechas es el mismo en toda la app).
 * Este hook es local a cada pantalla: cada una persiste sus propios filtros
 * bajo su propio namespace, sin compartir estado entre pantallas distintas.
 */
export function usePersistedState<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const storageKey = `asi.filtros.${key}`;
  const initialRef = useRef(initialValue);

  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return initialRef.current;
      return JSON.parse(raw) as T;
    } catch {
      return initialRef.current;
    }
  });

  const setPersisted = (value: T) => {
    setState(value);
    try {
      // Si el valor vuelve al default inicial, se limpia la clave en vez de
      // guardar el default explícito — evita acumular entradas de
      // localStorage para filtros que el usuario nunca tocó realmente.
      if (JSON.stringify(value) === JSON.stringify(initialRef.current)) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(value));
      }
    } catch {
      // Nada que hacer si localStorage no está disponible — el filtro
      // simplemente no persiste entre visitas, pero sigue funcionando en
      // memoria durante la sesión actual.
    }
  };

  return [state, setPersisted];
}

/** Limpia todas las claves persistidas de un namespace (ej. al usar el
 * botón "Limpiar Filtros" de una pantalla) — evita que queden filtros
 * viejos en localStorage cuando el usuario explícitamente pide resetear. */
export function clearPersistedNamespace(prefix: string): void {
  try {
    const fullPrefix = `asi.filtros.${prefix}`;
    const keys = Object.keys(localStorage).filter(k => k.startsWith(fullPrefix));
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // Sin localStorage, no hay nada que limpiar.
  }
}
